import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';

/**
 * GenerationProgress — animated indicator for long async tasks (LLM/song gen).
 *
 * Props:
 *  - active   : boolean — show/hide the indicator
 *  - kind     : 'lyrics' | 'song'   — picks the right status messages
 *  - lang     : 'fr' | 'en'
 *  - estimate : approximate duration in seconds (default 25)
 */
const COPY = {
  lyrics: {
    fr: [
      'Connexion au modèle IA…',
      'Analyse du style et de l\u2019ambiance…',
      'Recherche de rimes et de cadence…',
      'Écriture du couplet 1…',
      'Composition du refrain…',
      'Affinage des images poétiques…',
      'Quelques touches finales…',
    ],
    en: [
      'Connecting to the AI model…',
      'Analyzing style and mood…',
      'Crafting rhymes and cadence…',
      'Writing verse 1…',
      'Building the chorus…',
      'Polishing imagery…',
      'Final touches…',
    ],
  },
  song: {
    fr: [
      'Préparation de la session studio…',
      'Sélection des sons et percussions…',
      'Mixage de l\u2019instru…',
      'Posage de la voix…',
      'Mastering en cours…',
      'Préparation des stems…',
      'Presque prêt…',
    ],
    en: [
      'Spinning up the studio session…',
      'Picking sounds and percussion…',
      'Mixing the instrumental…',
      'Laying down the vocals…',
      'Mastering the track…',
      'Preparing stems…',
      'Almost ready…',
    ],
  },
};

export default function GenerationProgress({ active, kind = 'lyrics', lang = 'fr', estimate = 25 }) {
  const messages = useMemo(() => (COPY[kind] || COPY.lyrics)[lang] || COPY[kind].en, [kind, lang]);
  const [pct, setPct] = useState(0);
  const [idx, setIdx] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setPct(0); setIdx(0); startRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    startRef.current = performance.now();
    const tick = () => {
      const elapsed = (performance.now() - startRef.current) / 1000;
      // ease-out asymptotic to 95% over `estimate` seconds (never reaches 100 until parent flips active off)
      const p = Math.min(95, 95 * (1 - Math.exp(-elapsed / (estimate * 0.5))));
      setPct(p);
      const mi = Math.min(messages.length - 1, Math.floor((p / 95) * messages.length));
      setIdx(mi);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active, estimate, messages]);

  if (!active) return null;
  const tip = kind === 'lyrics'
    ? (lang === 'fr' ? "Ça peut prendre 15 à 30 secondes selon la charge du modèle." : 'May take 15-30 seconds depending on model load.')
    : (lang === 'fr' ? "Génération immédiate (placeholder audio — bientôt vraie génération)." : 'Instant placeholder (real music generation coming soon).');

  return (
    <div className="rounded-xl border border-white/10 bg-[#16161a] p-4 mt-3">
      <div className="flex items-center gap-2 text-sm text-zinc-200">
        <Loader2 size={16} className="animate-spin text-pink-400" />
        <span className="font-medium">{messages[idx]}</span>
        <Sparkles size={14} className="ml-auto text-yellow-300 opacity-80" />
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #ff3ea5 0%, #ff8a3d 50%, #ffd23d 100%)',
            transition: 'width 120ms linear',
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
        <span>{tip}</span>
        <span className="tabular-nums">{Math.round(pct)}%</span>
      </div>
    </div>
  );
}
